import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useAuthFetch } from "../hooks/useAuthFetch";
import NavbarGenitore from "./NavbarGenitore";
import "../stili/DashboardGenitore.css";

const API_URL = process.env.REACT_APP_API_URL;

function formatDate(value) {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleDateString("it-IT");
  } catch {
    return value;
  }
}

function formatCurrency(value) {
  const num = Number(value ?? 0);
  return `€ ${num.toFixed(2)}`;
}

function getBadgeClass(stato) {
  switch ((stato || "").toUpperCase()) {
    case "PAGATO":
      return "dashboard-genitore-badge pagato";
    case "PARZIALE":
      return "dashboard-genitore-badge parziale";
    case "SCADUTO":
      return "dashboard-genitore-badge scaduto";
    case "NON_PAGATO":
      return "dashboard-genitore-badge non-pagato";
    default:
      return "dashboard-genitore-badge";
  }
}

async function readErrorResponse(res, fallbackMessage) {
  try {
    const text = await res.text();
    return text ? `${fallbackMessage}: ${res.status} - ${text}` : `${fallbackMessage}: ${res.status}`;
  } catch {
    return `${fallbackMessage}: ${res.status}`;
  }
}

function SchedaFiglioGenitore({ figlio }) {
  if (!figlio) return null;

  return (
    <div className="dashboard-genitore-card">
      <div className="dashboard-genitore-card-header">
        <h3>Dati atleta</h3>
        <span className={getBadgeClass(figlio.stato)}>{figlio.stato || "-"}</span>
      </div>

      <div className="dashboard-genitore-grid">
        <div className="dashboard-genitore-info-box">
          <span className="dashboard-genitore-label">Cognome</span>
          <span className="dashboard-genitore-value">{figlio.cognome || "-"}</span>
        </div>

        <div className="dashboard-genitore-info-box">
          <span className="dashboard-genitore-label">Nome</span>
          <span className="dashboard-genitore-value">{figlio.nome || "-"}</span>
        </div>

        <div className="dashboard-genitore-info-box">
          <span className="dashboard-genitore-label">Categoria</span>
          <span className="dashboard-genitore-value">{figlio.categoria || "-"}</span>
        </div>

        <div className="dashboard-genitore-info-box">
          <span className="dashboard-genitore-label">Stagione</span>
          <span className="dashboard-genitore-value">{figlio.stagione || "-"}</span>
        </div>

        <div className="dashboard-genitore-info-box">
          <span className="dashboard-genitore-label">Data nascita</span>
          <span className="dashboard-genitore-value">{formatDate(figlio.dataNascita)}</span>
        </div>

        <div className="dashboard-genitore-info-box">
          <span className="dashboard-genitore-label">Numero tesserino</span>
          <span className="dashboard-genitore-value">{figlio.numeroTesserino || "-"}</span>
        </div>
      </div>
    </div>
  );
}

function SchedaPagamentiGenitore({ pagamenti }) {
  if (!pagamenti) return null;

  const quote = Array.isArray(pagamenti.quote) ? pagamenti.quote : [];

  const quoteDaMostrare = quote.filter((q) => {
    const stato = String(q?.stato || "").toUpperCase();
    return stato !== "PAGATO";
  });

  const tuttePagate = quote.length > 0 && quoteDaMostrare.length === 0;

  return (
    <div className="dashboard-genitore-card">
      <div className="dashboard-genitore-card-header">
        <h3>Situazione pagamenti</h3>
        <span className={getBadgeClass(pagamenti.stato)}>{pagamenti.stato || "-"}</span>
      </div>

      <div className="dashboard-genitore-grid dashboard-genitore-grid-top">
        <div className="dashboard-genitore-info-box">
          <span className="dashboard-genitore-label">Totale dovuto</span>
          <span className="dashboard-genitore-value">
            {formatCurrency(pagamenti.totaleDovuto)}
          </span>
        </div>

        <div className="dashboard-genitore-info-box">
          <span className="dashboard-genitore-label">Totale pagato</span>
          <span className="dashboard-genitore-value">
            {formatCurrency(pagamenti.totalePagato)}
          </span>
        </div>

        <div className="dashboard-genitore-info-box">
          <span className="dashboard-genitore-label">Residuo</span>
          <span className="dashboard-genitore-value">
            {formatCurrency(pagamenti.residuo)}
          </span>
        </div>

        <div className="dashboard-genitore-info-box">
          <span className="dashboard-genitore-label">Stato complessivo</span>
          <span className="dashboard-genitore-value">{pagamenti.stato || "-"}</span>
        </div>
      </div>

      <div className="dashboard-genitore-subtitle">Quote ancora aperte</div>

      {quote.length === 0 ? (
        <div className="dashboard-genitore-empty">
          Nessuna quota presente per la stagione attiva.
        </div>
      ) : tuttePagate ? (
        <div className="dashboard-genitore-empty">
          Tutte le quote della stagione attiva risultano pagate.
        </div>
      ) : (
        <div className="dashboard-genitore-lista">
          {quoteDaMostrare.map((q, index) => (
            <div className="dashboard-genitore-item" key={q.idQuota ?? index}>
              <div className="dashboard-genitore-item-top">
                <strong>{q.descrizione || "QUOTA"}</strong>
                <span className={getBadgeClass(q.stato)}>{q.stato || "-"}</span>
              </div>

              <div className="dashboard-genitore-grid">
                <div className="dashboard-genitore-info-box mini">
                  <span className="dashboard-genitore-label">Scadenza</span>
                  <span className="dashboard-genitore-value">
                    {formatDate(q.dataScadenza)}
                  </span>
                </div>

                <div className="dashboard-genitore-info-box mini">
                  <span className="dashboard-genitore-label">Importo previsto</span>
                  <span className="dashboard-genitore-value">
                    {formatCurrency(q.importoPrevisto)}
                  </span>
                </div>

                <div className="dashboard-genitore-info-box mini">
                  <span className="dashboard-genitore-label">Importo dovuto</span>
                  <span className="dashboard-genitore-value">
                    {formatCurrency(q.importoDovuto)}
                  </span>
                </div>

                <div className="dashboard-genitore-info-box mini">
                  <span className="dashboard-genitore-label">Importo pagato</span>
                  <span className="dashboard-genitore-value">
                    {formatCurrency(q.importoPagato)}
                  </span>
                </div>

                <div className="dashboard-genitore-info-box mini">
                  <span className="dashboard-genitore-label">Residuo</span>
                  <span className="dashboard-genitore-value">
                    {formatCurrency(q.residuo)}
                  </span>
                </div>

                <div className="dashboard-genitore-info-box mini">
                  <span className="dashboard-genitore-label">Categoria</span>
                  <span className="dashboard-genitore-value">{q.categoria || "-"}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SchedaEventiGenitore({ eventi }) {
  const lista = useMemo(() => (Array.isArray(eventi) ? eventi : []), [eventi]);

  return (
    <div className="dashboard-genitore-card">
      <div className="dashboard-genitore-card-header">
        <h3>Prossimi eventi</h3>
      </div>

      {lista.length === 0 ? (
        <div className="dashboard-genitore-empty">
          Nessun evento disponibile per l’atleta.
        </div>
      ) : (
        <div className="dashboard-genitore-lista">
          {lista.map((evento, index) => (
            <div className="dashboard-genitore-item" key={index}>
              <div className="dashboard-genitore-item-top">
                <strong>{evento.tipo || "-"}</strong>
                {evento.categoria && (
                  <span className="dashboard-genitore-badge">{evento.categoria}</span>
                )}
              </div>

              <div className="dashboard-genitore-grid">
                <div className="dashboard-genitore-info-box mini">
                  <span className="dashboard-genitore-label">Data</span>
                  <span className="dashboard-genitore-value">
                    {formatDate(evento.data)}
                  </span>
                </div>

                <div className="dashboard-genitore-info-box mini">
                  <span className="dashboard-genitore-label">Orario</span>
                  <span className="dashboard-genitore-value">
                    {evento.oraInizio || "-"}
                    {evento.oraFine ? ` - ${evento.oraFine}` : ""}
                  </span>
                </div>

                <div className="dashboard-genitore-info-box mini">
                  <span className="dashboard-genitore-label">Indirizzo</span>
                  <span className="dashboard-genitore-value">
                    {evento.indirizzo || "-"}
                  </span>
                </div>

                {evento.avversario && (
                  <div className="dashboard-genitore-info-box mini">
                    <span className="dashboard-genitore-label">Avversario</span>
                    <span className="dashboard-genitore-value">
                      {evento.avversario}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DashboardGenitore() {
  const authFetch = useAuthFetch();

  const [figli, setFigli] = useState([]);
  const [idGiocatoreSelezionato, setIdGiocatoreSelezionato] = useState("");
  const [dashboard, setDashboard] = useState(null);
  const [loadingFigli, setLoadingFigli] = useState(false);
  const [loadingDashboard, setLoadingDashboard] = useState(false);

  const figlioSelezionato = useMemo(() => {
    return (
      figli.find((f) => String(f.idGiocatore) === String(idGiocatoreSelezionato)) || null
    );
  }, [figli, idGiocatoreSelezionato]);

  const fetchFigli = useCallback(async () => {
    setLoadingFigli(true);

    try {
      const res = await authFetch(`${API_URL}/api/genitore/dashboard/figli`);

      if (!res.ok) {
        throw new Error(await readErrorResponse(res, "Errore nel caricamento dei figli"));
      }

      const data = await res.json().catch(() => []);
      const lista = Array.isArray(data) ? data : [];

      setFigli(lista);

      if (lista.length > 0) {
        setIdGiocatoreSelezionato((prev) => (prev ? prev : String(lista[0].idGiocatore)));
      } else {
        setIdGiocatoreSelezionato("");
        setDashboard(null);
      }
    } catch (err) {
      console.error("Errore caricamento figli:", err);
      toast.error("Impossibile caricare i figli associati al genitore");
      setFigli([]);
      setIdGiocatoreSelezionato("");
      setDashboard(null);
    } finally {
      setLoadingFigli(false);
    }
  }, [authFetch]);

  const fetchDashboard = useCallback(
    async (idGiocatore) => {
      if (!idGiocatore) {
        setDashboard(null);
        return;
      }

      setLoadingDashboard(true);

      try {
        const res = await authFetch(`${API_URL}/api/genitore/dashboard/${idGiocatore}`);

        if (!res.ok) {
          throw new Error(
            await readErrorResponse(res, "Errore nel caricamento della dashboard")
          );
        }

        const data = await res.json().catch(() => null);
        setDashboard(data);
      } catch (err) {
        console.error("Errore dashboard genitore:", err);
        toast.error("Impossibile caricare i dati della dashboard genitore");
        setDashboard(null);
      } finally {
        setLoadingDashboard(false);
      }
    },
    [authFetch]
  );

  useEffect(() => {
    fetchFigli();
  }, [fetchFigli]);

  useEffect(() => {
    if (!idGiocatoreSelezionato) {
      setDashboard(null);
      return;
    }

    fetchDashboard(idGiocatoreSelezionato);
  }, [idGiocatoreSelezionato, fetchDashboard]);

  return (
    <div className="dashboard-genitore-page">
      <div className="dashboard-genitore-layout">
        <NavbarGenitore />

        <div className="dashboard-genitore-container">
          <div className="dashboard-genitore-welcome">
            <div className="dashboard-genitore-welcome-text">
              <h1>Benvenuto nell’area genitore</h1>
              <p>
                Da questa dashboard puoi consultare rapidamente i dati del tuo atleta,
                la situazione pagamenti e i prossimi eventi in programma.
              </p>
            </div>

            <div className="dashboard-genitore-welcome-side">
              <div className="dashboard-genitore-select-box">
                <label htmlFor="figlioSelect">Seleziona figlio</label>
                <select
                  id="figlioSelect"
                  value={idGiocatoreSelezionato}
                  onChange={(e) => setIdGiocatoreSelezionato(e.target.value)}
                  disabled={loadingFigli || figli.length === 0}
                >
                  {figli.length === 0 ? (
                    <option value="">Nessun figlio disponibile</option>
                  ) : (
                    figli.map((figlio) => (
                      <option key={figlio.idGiocatore} value={figlio.idGiocatore}>
                        {figlio.cognome} {figlio.nome}
                        {figlio.categoria ? ` - ${figlio.categoria}` : ""}
                      </option>
                    ))
                  )}
                </select>
              </div>

              {figlioSelezionato && (
                <div className="dashboard-genitore-welcome-athlete">
                  <span className="dashboard-genitore-athlete-name">
                    {figlioSelezionato.cognome} {figlioSelezionato.nome}
                  </span>
                  <span className="dashboard-genitore-athlete-meta">
                    {figlioSelezionato.categoria || "Categoria non disponibile"}
                  </span>
                </div>
              )}
            </div>
          </div>

          {(loadingFigli || loadingDashboard) && (
            <div className="dashboard-genitore-loading">Caricamento in corso...</div>
          )}

          {!loadingFigli && !loadingDashboard && !dashboard && figli.length === 0 && (
            <div className="dashboard-genitore-empty">
              Nessun atleta associato a questo genitore.
            </div>
          )}

          {!loadingDashboard && dashboard && (
            <div className="dashboard-genitore-sezioni">
              <SchedaFiglioGenitore figlio={dashboard.figlio} />
              <SchedaPagamentiGenitore pagamenti={dashboard.pagamenti} />
              <SchedaEventiGenitore eventi={dashboard.eventi} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}